import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "../../components/ui/card";
import { Progress } from "../../components/ui/progress";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import {
  Clock, CalendarDays, Coins, ClipboardCheck, FolderOpen,
  CheckCircle, AlertCircle, ArrowRight,
} from "lucide-react";
import api from "../../lib/api";

const MyDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const res = await api.get("/self-service/dashboard");
        setData(res.data);
      } catch (err) {
        toast.error(err.response?.data?.detail || "Failed to load dashboard");
      } finally {
        setLoading(false);
      }
    };
    fetchDashboard();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="animate-spin h-10 w-10 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-20 max-w-md mx-auto" data-testid="no-profile-msg">
        <div className="w-16 h-16 rounded-2xl bg-amber-100 flex items-center justify-center mx-auto mb-4">
          <AlertCircle className="h-8 w-8 text-amber-600" />
        </div>
        <p className="text-lg font-medium text-slate-800">No employee profile found</p>
        <p className="text-sm text-slate-500 mt-2">
          Your account is not linked to an employee record yet. Ask your administrator to create your employee profile using your login email.
        </p>
      </div>
    );
  }

  const { employee, attendance, leaves, latest_payslip, onboarding_progress, has_active_onboarding, document_count } = data;

  return (
    <div className="space-y-6" data-testid="my-dashboard">
      {/* Welcome */}
      <div>
        <h1 className="text-2xl font-bold text-slate-800 font-heading">
          Welcome, {employee.first_name}
        </h1>
        <p className="text-slate-500">
          {employee.employee_code ? `Employee Code: ${employee.employee_code}` : "Your self-service portal"}
        </p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Attendance */}
        <Card
          className="stat-card cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => navigate("/my-attendance")}
          data-testid="stat-attendance"
        >
          <CardContent className="p-0">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-blue-100">
                <Clock className="h-6 w-6 text-blue-700" />
              </div>
              <div className="flex-1">
                <p className="text-2xl font-bold text-slate-800">{attendance.present_this_month}</p>
                <p className="text-sm text-slate-500">Days This Month</p>
              </div>
              {attendance.checked_in_today ? (
                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 text-xs">
                  <CheckCircle className="h-3 w-3 mr-1" /> In
                </Badge>
              ) : (
                <Badge variant="outline" className="bg-slate-50 text-slate-500 border-slate-200 text-xs">
                  Not In
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Leaves */}
        <Card
          className="stat-card cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => navigate("/my-leaves")}
          data-testid="stat-leaves"
        >
          <CardContent className="p-0">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-amber-100">
                <CalendarDays className="h-6 w-6 text-amber-700" />
              </div>
              <div className="flex-1">
                <p className="text-2xl font-bold text-slate-800">{leaves.total}</p>
                <p className="text-sm text-slate-500">Leave Requests</p>
              </div>
              {leaves.pending > 0 && (
                <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-xs">
                  <AlertCircle className="h-3 w-3 mr-1" /> {leaves.pending}
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Latest Payslip */}
        <Card
          className="stat-card cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => navigate("/my-payslips")}
          data-testid="stat-payslips"
        >
          <CardContent className="p-0">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-green-100">
                <Coins className="h-6 w-6 text-green-700" />
              </div>
              <div className="flex-1">
                {latest_payslip ? (
                  <>
                    <p className="text-lg font-bold text-slate-800">{latest_payslip.month}/{latest_payslip.year}</p>
                    <p className="text-sm text-slate-500">Latest Payslip</p>
                  </>
                ) : (
                  <>
                    <p className="text-lg font-bold text-slate-400">--</p>
                    <p className="text-sm text-slate-500">No Payslips</p>
                  </>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Documents */}
        <Card
          className="stat-card cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => navigate("/my-documents")}
          data-testid="stat-documents"
        >
          <CardContent className="p-0">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-violet-100">
                <FolderOpen className="h-6 w-6 text-violet-700" />
              </div>
              <div className="flex-1">
                <p className="text-2xl font-bold text-slate-800">{document_count}</p>
                <p className="text-sm text-slate-500">Documents</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Onboarding Progress */}
      {has_active_onboarding && (
        <Card
          className="card-soft cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => navigate("/my-onboarding")}
          data-testid="onboarding-card"
        >
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-xl bg-teal-100">
                  <ClipboardCheck className="h-5 w-5 text-teal-700" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-800">Onboarding Progress</h3>
                  <p className="text-sm text-slate-500">{onboarding_progress}% complete</p>
                </div>
              </div>
              <ArrowRight className="h-5 w-5 text-slate-400" />
            </div>
            <Progress value={onboarding_progress} className="h-2.5" />
          </CardContent>
        </Card>
      )}

      {/* Quick Actions */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card
          className="card-soft cursor-pointer hover:shadow-md transition-all group"
          onClick={() => navigate("/my-leaves")}
          data-testid="action-apply-leave"
        >
          <CardContent className="p-5 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-primary/10 group-hover:bg-primary/20 transition-colors">
              <CalendarDays className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="font-medium text-slate-800">Apply for Leave</p>
              <p className="text-xs text-slate-500">Submit a new request</p>
            </div>
          </CardContent>
        </Card>
        <Card
          className="card-soft cursor-pointer hover:shadow-md transition-all group"
          onClick={() => navigate("/my-profile")}
          data-testid="action-update-profile"
        >
          <CardContent className="p-5 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-primary/10 group-hover:bg-primary/20 transition-colors">
              <CheckCircle className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="font-medium text-slate-800">Update Profile</p>
              <p className="text-xs text-slate-500">Edit personal info</p>
            </div>
          </CardContent>
        </Card>
        <Card
          className="card-soft cursor-pointer hover:shadow-md transition-all group"
          onClick={() => navigate("/my-payslips")}
          data-testid="action-view-payslips"
        >
          <CardContent className="p-5 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-primary/10 group-hover:bg-primary/20 transition-colors">
              <Coins className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="font-medium text-slate-800">View Payslips</p>
              <p className="text-xs text-slate-500">Download salary slips</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default MyDashboard;
